/**
 * BatteryMeter - Displays flashlight/device battery level
 */

import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';

interface BatteryMeterProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  showPercentage?: boolean;
}

export const BatteryMeter: React.FC<BatteryMeterProps> = ({
  position = 'top-right',
  showPercentage = true
}) => {
  const flashlightBattery = useSelector((state: RootState) => state.player.flashlightBattery);

  // Determine color based on battery level
  const getBatteryColor = () => {
    if (flashlightBattery > 50) return '#00ff00'; // Green
    if (flashlightBattery > 20) return '#ffaa00'; // Orange
    return '#ff0000'; // Red - critical
  };

  const positionStyles = {
    'top-left': { top: '20px', left: '20px' },
    'top-right': { top: '20px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
  };

  return (
    <div style={{
      position: 'absolute',
      ...positionStyles[position],
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      fontFamily: 'monospace',
      color: '#ffffff',
      pointerEvents: 'none',
      userSelect: 'none',
    }}>
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>BATTERY</div>
      <div style={{
        width: '150px',
        height: '30px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        border: '2px solid #444',
        borderRadius: '6px',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}>
        {/* Battery fill */}
        <div style={{
          width: `${flashlightBattery}%`,
          height: '100%',
          backgroundColor: getBatteryColor(),
          transition: 'width 0.5s ease, background-color 0.3s ease',
        }} />

        {/* Battery segments (visual effect) */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
        }}>
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                borderRight: i < 9 ? '1px solid rgba(0, 0, 0, 0.3)' : 'none',
              }}
            />
          ))}
        </div>

        {/* Battery percentage */}
        {showPercentage && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
          }}>
            {Math.round(flashlightBattery)}%
          </div>
        )}

        {/* Low battery warning */}
        {flashlightBattery < 20 && (
          <div style={{
            position: 'absolute',
            top: '-25px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '12px',
            color: '#ff0000',
            fontWeight: 'bold',
            animation: 'blink 1s infinite',
          }}>
            LOW BATTERY!
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}
      </style>
    </div>
  );
};
